import React, { useState, useEffect } from 'react';
import { db, auth, getCompanyId } from '../firebase';
import { collection, onSnapshot, query, doc, getDoc, updateDoc, addDoc, where } from 'firebase/firestore';
import Navbar from '../components/Navbar';
import { typeLabel, formatRange } from '../timeOff';

export default function Dashboard() {
  const [employees, setEmployees] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [timeOffRequests, setTimeOffRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('employees');

  useEffect(() => {
    const setup = async () => {
      const companyId = await getCompanyId();

      const unsubUsers = onSnapshot(query(collection(db, 'users'), where('companyId', '==', companyId)), (snapshot) => {
        const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        setEmployees(data.filter(u => u.role === 'employee'));
      });

      const unsubEntries = onSnapshot(query(collection(db, 'timeEntries'), where('companyId', '==', companyId)), (snapshot) => {
        const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        setTimeEntries(data);
        setLoading(false);
      });

      const unsubTimeOff = onSnapshot(query(collection(db, 'timeOffRequests'), where('companyId', '==', companyId)), (snapshot) => {
        const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        data.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
        setTimeOffRequests(data);
      });

      return () => { unsubUsers(); unsubEntries(); unsubTimeOff(); };
    };
    setup();
  }, []);

  const getStatus = (employeeId) => {
    const entries = timeEntries.filter(e => e.userId === employeeId);
    const last = entries.sort((a, b) => b.clockIn?.seconds - a.clockIn?.seconds)[0];
    if (!last) return 'never';
    if (last.clockIn && !last.clockOut) {
      if (last.onBreak) return 'break';
      return 'in';
    }
    return 'out';
  };

  const getName = (userId) => {
    const emp = employees.find(e => e.id === userId);
    return emp ? emp.name : 'Unknown';
  };

  const lateEntries = timeEntries.filter(e => e.status === 'late' && !e.pardoned);
  const noShiftEntries = timeEntries.filter(e => e.status === 'no-shift' && !e.pardoned);
  const earlyInEntries = timeEntries.filter(e => e.status === 'early' && !e.pardoned);
  const earlyEntries = timeEntries.filter(e => e.earlyClockOut === true && !e.earlyPardoned);
  const overtimeEntries = timeEntries.filter(e => e.overtime === true);
  const pendingTimeOff = timeOffRequests.filter(r => r.status === 'pending');
  const reviewedTimeOff = timeOffRequests.filter(r => r.status !== 'pending');


  const totalPending = lateEntries.length + earlyInEntries.length + earlyEntries.length + noShiftEntries.length + overtimeEntries.length + pendingTimeOff.length;

  // Best-effort push to the employee's phone; the web admin has no server,
  // so a CORS or network failure here must never block the decision itself.
  const notifyUser = async (userId, title, body) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      const token = userDoc.data()?.pushToken;
      if (!token) return;
      await fetch('https://exp.host/--/api/v2/push/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify([{ to: token, title, body, sound: 'default' }]),
      });
    } catch (e) {
      console.warn('Push notification failed:', e.message);
    }
  };

  const handleTimeOffDecision = async (request, approve) => {
    const name = request.userName || getName(request.userId);
    const label = typeLabel(request.type).toLowerCase();
    const range = formatRange(request.startDate, request.endDate);
    if (!window.confirm(`${approve ? 'Approve' : 'Deny'} ${name}'s ${label} request for ${range}?`)) return;
    await updateDoc(doc(db, 'timeOffRequests', request.id), {
      status: approve ? 'approved' : 'denied',
      reviewedAt: new Date(),
      reviewedBy: auth.currentUser.uid,
    });
    notifyUser(
      request.userId,
      approve ? 'Time Off Approved' : 'Time Off Denied',
      `Your ${label} request (${range}) was ${approve ? 'approved' : 'denied'}.`
    );
  };

  const handlePardon = async (entryId) => {
    await updateDoc(doc(db, 'timeEntries', entryId), {
      pardoned: true,
      pardonedAt: new Date(),
      pardonedBy: auth.currentUser.uid,
    });
  };

  const handleEarlyPardon = async (entryId) => {
    await updateDoc(doc(db, 'timeEntries', entryId), {
      earlyPardoned: true,
      earlyPardonedAt: new Date(),
      earlyPardonedBy: auth.currentUser.uid,
    });
  };

  const handleManualClockIn = async (employeeId, employeeName) => {
    if (window.confirm(`Manually clock in ${employeeName}?`)) {
      const companyId = await getCompanyId();
      await addDoc(collection(db, 'timeEntries'), {
        userId: employeeId,
        clockIn: new Date(),
        clockOut: null,
        status: 'on-time',
        manualEntry: true,
        date: new Date().toDateString(),
        lateReason: null,
        pardoned: false,
        earlyPardoned: false,
        onBreak: false,
        breaks: [],
        companyId,
      });
    }
  };

  const handleManualClockOut = async (employeeId, employeeName, entry) => {
    if (!entry) return;
    if (window.confirm(`Manually clock out ${employeeName}?`)) {
      await updateDoc(doc(db, 'timeEntries', entry.id), {
        clockOut: new Date(),
        manualClockOut: true,
      });
    }
  };

  const formatTime = (seconds) => {
    if (!seconds) return '--';
    const date = new Date(seconds * 1000);
    const hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours < 12 ? 'AM' : 'PM';
    const displayHour = hours % 12 === 0 ? 12 : hours % 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const tabs = [
    { key: 'employees', label: 'Staff' },
    { key: 'late', label: `Late${lateEntries.length > 0 ? ` (${lateEntries.length})` : ''}` },
    { key: 'early', label: `Early${(earlyInEntries.length + earlyEntries.length) > 0 ? ` (${earlyInEntries.length + earlyEntries.length})` : ''}` },
    { key: 'noshift', label: `No Shift${noShiftEntries.length > 0 ? ` (${noShiftEntries.length})` : ''}` },
    { key: 'overtime', label: `OT${overtimeEntries.length > 0 ? ` (${overtimeEntries.length})` : ''}` },
    { key: 'timeoff', label: `Leave${pendingTimeOff.length > 0 ? ` (${pendingTimeOff.length})` : ''}` },
  ];

  return (
    <div style={styles.page}>
      <Navbar />
      <div style={styles.container}>
        <h1 style={styles.title}>Dashboard</h1>

        <div style={styles.statsRow}>
          <div style={styles.statBox}>
            <div style={styles.statNumber}>{employees.filter(e => getStatus(e.id) === 'in' || getStatus(e.id) === 'break').length}</div>
            <div style={styles.statLabel}>Clocked In</div>
          </div>
          <div style={styles.statBox}>
            <div style={styles.statNumber}>{employees.filter(e => getStatus(e.id) === 'out').length}</div>
            <div style={styles.statLabel}>Clocked Out</div>
          </div>
          <div style={styles.statBox}>
            <div style={{ ...styles.statNumber, ...(totalPending > 0 ? { color: '#f87171' } : {}) }}>
              {totalPending}
            </div>
            <div style={styles.statLabel}>Pending</div>
          </div>
          <div style={styles.statBox}>
            <div style={styles.statNumber}>{employees.length}</div>
            <div style={styles.statLabel}>Total Staff</div>
          </div>
        </div>

        <div style={styles.tabRow}>
          {tabs.map(tab => (
            <button
              key={tab.key}
              style={{ ...styles.tab, ...(activeTab === tab.key ? styles.tabActive : {}) }}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={styles.empty}>Loading...</div>
        ) : activeTab === 'employees' ? (
          <table style={styles.table}>
            <thead>
              <tr>
                  {['Name', 'Email', 'Status', 'Last Clock In', 'Actions'].map(h => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {employees.map(emp => {
                const status = getStatus(emp.id);
                const lastEntry = timeEntries
                  .filter(e => e.userId === emp.id)
                  .sort((a, b) => b.clockIn?.seconds - a.clockIn?.seconds)[0];
                return (
                  <tr key={emp.id} style={styles.tr}>
                    <td style={styles.td}>{emp.name}</td>
                    <td style={styles.td}>{emp.email}</td>
                   <td style={styles.td}>
                    <span style={{
                      ...styles.badge,
                      backgroundColor: status === 'in' ? '#14532d' : status === 'break' ? '#431407' : status === 'never' ? '#2a2a2a' : '#2d1414',
                      color: status === 'in' ? '#4ade80' : status === 'break' ? '#fb923c' : status === 'never' ? '#999' : '#f87171',
                    }}>
                      {status === 'in' ? '● In' : status === 'break' ? '● Break' : status === 'never' ? 'Never' : '○ Out'}
                    </span>
                  </td>
                  <td style={styles.td}>{lastEntry ? formatTime(lastEntry.clockIn?.seconds) : '--'}</td>
                  <td style={styles.td}>
                    <div style={styles.actions}>
                      {status !== 'in' ? (
                        <button
                          style={styles.clockInBtn}
                          onClick={() => handleManualClockIn(emp.id, emp.name)}
                        >
                          Clock In
                        </button>
                      ) : (
                        <button
                          style={styles.clockOutBtn}
                          onClick={() => handleManualClockOut(emp.id, emp.name, lastEntry)}
                        >
                          Clock Out
                        </button>
                      )}
                    </div>
                  </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : activeTab === 'late' ? (
          <table style={styles.table}>
            <thead>
              <tr>
                {['Employee', 'Date', 'Reason', 'Action'].map(h => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lateEntries.length === 0 ? (
                <tr><td colSpan={4} style={{ ...styles.td, textAlign: 'center', color: '#999' }}>No late employees!</td></tr>
              ) : lateEntries.map(entry => (
                <tr key={entry.id} style={styles.tr}>
                  <td style={styles.td}>{getName(entry.userId)}</td>
                  <td style={styles.td}>{entry.date}</td>
                  <td style={{ ...styles.td, color: '#f87171', fontStyle: 'italic' }}>
                    {entry.status === 'no-shift' ? '⚠️ No shift: ' : ''}"{entry.lateReason}"
                  </td>
                  <td style={styles.td}>
                    <button style={styles.pardonBtn} onClick={() => handlePardon(entry.id)}>Pardon</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : activeTab === 'early' ? (
          <table style={styles.table}>
            <thead>
              <tr>
                {['Employee', 'Date', 'Type', 'Reason', 'Action'].map(h => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {earlyInEntries.length === 0 && earlyEntries.length === 0 ? (
                <tr><td colSpan={5} style={{ ...styles.td, textAlign: 'center', color: '#999' }}>No early clock ins or outs!</td></tr>
              ) : (
                <>
                  {earlyInEntries.map(entry => (
                    <tr key={entry.id + '-in'} style={styles.tr}>
                      <td style={styles.td}>{getName(entry.userId)}</td>
                      <td style={styles.td}>{entry.date}</td>
                      <td style={styles.td}>Clock In</td>
                      <td style={{ ...styles.td, color: '#f87171', fontStyle: 'italic' }}>"{entry.earlyClockInReason}"</td>
                      <td style={styles.td}>
                        <button style={styles.pardonBtn} onClick={() => handlePardon(entry.id)}>Pardon</button>
                      </td>
                    </tr>
                  ))}
                  {earlyEntries.map(entry => (
                    <tr key={entry.id + '-out'} style={styles.tr}>
                      <td style={styles.td}>{getName(entry.userId)}</td>
                      <td style={styles.td}>{entry.date}</td>
                      <td style={styles.td}>Clock Out</td>
                      <td style={{ ...styles.td, color: '#f87171', fontStyle: 'italic' }}>"{entry.earlyClockOutReason}"</td>
                      <td style={styles.td}>
                        <button style={styles.pardonBtn} onClick={() => handleEarlyPardon(entry.id)}>Allow</button>
                      </td>
                    </tr>
                  ))}
                </>
              )}
            </tbody>
          </table>
        ) : activeTab === 'noshift' ? (
          <table style={styles.table}>
            <thead>
              <tr>
                {['Employee', 'Date', 'Reason', 'Action'].map(h => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {noShiftEntries.length === 0 ? (
                <tr><td colSpan={4} style={{ ...styles.td, textAlign: 'center', color: '#999' }}>No unscheduled clock ins!</td></tr>
              ) : noShiftEntries.map(entry => (
                <tr key={entry.id} style={styles.tr}>
                  <td style={styles.td}>{getName(entry.userId)}</td>
                  <td style={styles.td}>{entry.date}</td>
                  <td style={{ ...styles.td, color: '#f87171', fontStyle: 'italic' }}>"{entry.lateReason}"</td>
                  <td style={styles.td}>
                    <button style={styles.pardonBtn} onClick={() => handlePardon(entry.id)}>Pardon</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : activeTab === 'overtime' ? (
          <table style={styles.table}>
            <thead>
              <tr>
                {['Employee', 'Date', 'Overtime Hours'].map(h => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {overtimeEntries.length === 0 ? (
                <tr><td colSpan={3} style={{ ...styles.td, textAlign: 'center', color: '#999' }}>No overtime!</td></tr>
              ) : overtimeEntries.map(entry => (
                <tr key={entry.id} style={styles.tr}>
                  <td style={styles.td}>{getName(entry.userId)}</td>
                  <td style={styles.td}>{entry.date}</td>
                  <td style={{ ...styles.td, color: '#fb923c' }}>+{entry.overtimeHours}h</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <>
            <table style={styles.table}>
              <thead>
                <tr>
                  {['Employee', 'Type', 'Dates', 'Days', 'Reason', 'Action'].map(h => (
                    <th key={h} style={styles.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pendingTimeOff.length === 0 ? (
                  <tr><td colSpan={6} style={{ ...styles.td, textAlign: 'center', color: '#999' }}>No pending time-off requests!</td></tr>
                ) : pendingTimeOff.map(request => (
                  <tr key={request.id} style={styles.tr}>
                    <td style={styles.td}>{request.userName || getName(request.userId)}</td>
                    <td style={styles.td}>{typeLabel(request.type)}</td>
                    <td style={styles.td}>{formatRange(request.startDate, request.endDate)}</td>
                    <td style={styles.td}>{request.days} day{request.days === 1 ? '' : 's'}</td>
                    <td style={{ ...styles.td, color: '#999', fontStyle: 'italic' }}>
                      {request.reason ? `"${request.reason}"` : '--'}
                    </td>
                    <td style={styles.td}>
                      <div style={styles.actions}>
                        <button style={styles.pardonBtn} onClick={() => handleTimeOffDecision(request, true)}>Approve</button>
                        <button style={styles.denyBtn} onClick={() => handleTimeOffDecision(request, false)}>Deny</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {reviewedTimeOff.length > 0 && (
              <>
                <div style={styles.reviewedHeader}>Reviewed</div>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      {['Employee', 'Type', 'Dates', 'Decision'].map(h => (
                        <th key={h} style={styles.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {reviewedTimeOff.slice(0, 10).map(request => (
                      <tr key={request.id} style={styles.tr}>
                        <td style={styles.td}>{request.userName || getName(request.userId)}</td>
                        <td style={styles.td}>{typeLabel(request.type)}</td>
                        <td style={styles.td}>{formatRange(request.startDate, request.endDate)}</td>
                        <td style={{
                          ...styles.td,
                          color: request.status === 'approved' ? '#4ade80' : '#f87171',
                          fontWeight: 'bold',
                        }}>
                          {request.status === 'approved' ? 'Approved' : 'Denied'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#0f0f0f',
  },
  container: {
    padding: '32px 40px',
    maxWidth: 1200,
    margin: '0 auto',
  },
  title: {
    color: '#fff',
    fontSize: 28,
    fontWeight: 'bold',
    marginBottom: 24,
    marginTop: 0,
  },
  clockInBtn: {
    backgroundColor: '#14532d',
    color: '#4ade80',
    border: 'none',
    borderRadius: 8,
    padding: '6px 14px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: 13,
  },
  clockOutBtn: {
    backgroundColor: '#2d1414',
    color: '#f87171',
    border: 'none',
    borderRadius: 8,
    padding: '6px 14px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: 13,
  },
  statsRow: {
    display: 'flex',
    gap: 16,
    marginBottom: 32,
  },
  statBox: {
    flex: 1,
    backgroundColor: '#1e1e1e',
    borderRadius: 12,
    padding: 20,
    textAlign: 'center',
  },
  statNumber: {
    fontSize: 32,
    fontWeight: 'bold',
    color: '#4f46e5',
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 13,
    color: '#999',
  },
  tabRow: {
    display: 'flex',
    gap: 8,
    marginBottom: 20,
    backgroundColor: '#1e1e1e',
    padding: 6,
    borderRadius: 12,
    width: 'fit-content',
  },
  tab: {
    backgroundColor: 'transparent',
    border: 'none',
    color: '#999',
    padding: '8px 18px',
    borderRadius: 8,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: '600',
  },
  tabActive: {
    backgroundColor: '#4f46e5',
    color: '#fff',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: '#1e1e1e',
    borderRadius: 12,
    overflow: 'hidden',
  },
  th: {
    padding: '14px 20px',
    textAlign: 'left',
    color: '#999',
    fontSize: 13,
    fontWeight: '600',
    borderBottom: '1px solid #2a2a2a',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  tr: {
    borderBottom: '1px solid #2a2a2a',
  },
  td: {
    padding: '14px 20px',
    color: '#fff',
    fontSize: 14,
  },
  badge: {
    padding: '4px 10px',
    borderRadius: 20,
    fontSize: 13,
    fontWeight: 'bold',
  },
  pardonBtn: {
    backgroundColor: '#14532d',
    color: '#4ade80',
    border: 'none',
    borderRadius: 8,
    padding: '6px 14px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: 13,
  },
  denyBtn: {
    backgroundColor: '#2d1414',
    color: '#f87171',
    border: 'none',
    borderRadius: 8,
    padding: '6px 14px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: 13,
  },
  actions: {
    display: 'flex',
    gap: 8,
  },
  reviewedHeader: {
    color: '#999',
    fontSize: 13,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    margin: '24px 0 10px',
  },
  empty: {
    color: '#999',
    textAlign: 'center',
    padding: 40,
  },
};